// The program's functions, types, labels and breakpoints, in a pane under the
// file list, as the Windows IDE's Funcs, Types, Labels and Breaks tabs show
// them. index() reads a source file as modern/ide/source_symbols.cpp does,
// so that the IDEs list the same things.

const IDENTIFIER_START = /\p{L}/u;
const IDENTIFIER = /[\p{L}\p{N}_]/u;
const SPACE = /\s/;

// "Private Worker Function Name" and the like: the name a declaration line
// declares with keyword, or "".
function declaration(line, keyword, allowPrivate = true, allowWorker = false) {
  let position = 0;
  const word = (text) => {
    while (position < line.length && SPACE.test(line[position])) ++position;
    if (line.slice(position, position + text.length).toLowerCase() !== text.toLowerCase()) return false;
    const end = position + text.length;
    if (end < line.length && IDENTIFIER.test(line[end])) return false;
    position = end;
    return true;
  };
  if (allowPrivate) {
    const saved = position;
    if (!word("Private")) position = saved;
  }
  if (allowWorker) {
    const saved = position;
    if (!word("Worker")) position = saved;
  }
  if (!word(keyword)) return "";
  if (position >= line.length || !SPACE.test(line[position])) return "";
  while (position < line.length && SPACE.test(line[position])) ++position;
  if (position >= line.length || !IDENTIFIER_START.test(line[position])) return "";
  const begin = position++;
  while (position < line.length && IDENTIFIER.test(line[position])) ++position;
  return line.slice(begin, position);
}

// The names a "Field a, b.Point, c$[4]" line declares.
function fieldNames(line) {
  const match = /^\s*(?:private\s+)?field(?=\s)/i.exec(line);
  if (!match) return [];
  const names = [];
  let position = match[0].length, quoted = false, parentheses = 0, brackets = 0;
  while (position < line.length) {
    while (position < line.length && SPACE.test(line[position])) ++position;
    const start = position;
    while (position < line.length && IDENTIFIER.test(line[position])) ++position;
    if (position > start && IDENTIFIER_START.test(line[start])) names.push(line.slice(start, position));
    for (; position < line.length; ++position) {
      const character = line[position];
      if (character === '"') quoted = !quoted;
      else if (!quoted) {
        if (character === ";") return names;
        if (character === "(") ++parentheses;
        else if (character === ")" && parentheses > 0) --parentheses;
        else if (character === "[") ++brackets;
        else if (character === "]" && brackets > 0) --brackets;
        else if (character === "," && parentheses === 0 && brackets === 0) {
          ++position;
          break;
        }
      }
    }
  }
  return names;
}

const label = (line) => {
  const match = /^\s*\.(\p{L}[\p{L}\p{N}_]*)/u.exec(line);
  return match ? match[1] : "";
};
const endType = (line) => /^\s*end\s+type(?![\p{L}\p{N}_])/iu.test(line);
// Extended's /* and */ lines, which comment out the lines between.
const blockDirective = (line) => {
  const match = /^[ \t\v\f\r]*(\/\*|\*\/)/.exec(line);
  return match ? { delta: match[1] === "/*" ? 1 : -1, to: match[0].length } : { delta: 0, to: 0 };
};

// A source file's symbols: { functions, types, labels }, each { name, line }
// with lines from 0; a type has fields and methods, and isEnum.
export function index(text) {
  const result = { functions: [], types: [], labels: [] };
  let depth = 0, type = null;
  text.split("\n").forEach((raw, line) => {
    const whole = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    const block = blockDirective(whole);
    let start = 0;
    if (depth) {
      if (block.delta > 0) { ++depth; return; }
      if (block.delta < 0) {
        if (--depth) return;
        start = block.to;
      } else {
        return;
      }
    } else if (block.delta > 0) {
      depth = 1;
      return;
    } else if (block.delta < 0) {
      return;
    }
    const visible = whole.slice(start);
    const functionName = declaration(visible, "Function", true, true);
    if (functionName) result.functions.push({ name: functionName, line });
    const typeName = declaration(visible, "Type");
    const enumName = typeName ? "" : declaration(visible, "Enum");
    if (typeName) {
      type = { name: typeName, line, fields: [], methods: [], isEnum: false };
      result.types.push(type);
    } else if (enumName) {
      result.types.push({ name: enumName, line, fields: [], methods: [], isEnum: true });
      type = null;
    } else if (type) {
      if (endType(visible)) type = null;
      else {
        const methodName = declaration(visible, "Method");
        if (methodName) type.methods.push({ name: methodName, line });
        else for (const name of fieldNames(visible)) type.fields.push({ name, line });
      }
    }
    const labelName = label(visible);
    if (labelName) result.labels.push({ name: labelName, line });
  });
  return result;
}

// The files a source Includes, as paths beside it: Include "lib\x.bb" from
// src/main.bb is src/lib/x.bb.
// The files a source file Includes or Imports, as paths in the project. A
// logical Import, such as Import simon.physics, names the module's file in
// the modules folder beside the program's main file (programFolder, "" or
// ending in /), in lower case, as the compiler finds it.
export function includes(path, text, programFolder = "") {
  const folder = path.includes("/") ? path.slice(0, path.lastIndexOf("/") + 1) : "";
  const resolve = (start, relative) => {
    const parts = [];
    for (const part of (start + relative.replace(/\\/g, "/")).split("/")) {
      if (part === "..") parts.pop();
      else if (part && part !== ".") parts.push(part);
    }
    return parts.join("/");
  };
  const found = [];
  for (const match of text.matchAll(/^\s*(?:Include|Import)\s+"([^"]+)"/gim)) found.push(resolve(folder, match[1]));
  for (const match of text.matchAll(/^\s*Import\s+([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)/gim)) {
    found.push(`${programFolder}modules/${match[1].toLowerCase().replace(/\./g, "/")}.bb`);
  }
  return found;
}

const PAGES = [["functions", "Funcs"], ["types", "Types"], ["labels", "Labels"], ["breakpoints", "Breaks"],
  ["find", "Find"]];

// The pane: four tabs over a list of the sources, each with its symbols, and
// a fifth for Find in Files, whose panel (findfiles.js) takes the list's
// place. show(path, line) opens a file at a line (from 0).
export class SymbolPane {
  constructor({ tabs, list, findPanel, show }) {
    this.tabsElement = tabs;
    this.list = list;
    this.findPanel = findPanel;
    this.show = show;
    this.page = "functions";
    this.sources = [];         // [{ path, symbols }]
    this.breakpoints = [];     // [{ path, rows: [{ row, text }] }], rows from 1
    this.closed = new Set();   // "page|path" of the sources collapsed
    for (const [page, title] of PAGES) {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.setAttribute("role", "tab");
      tab.dataset.page = page;
      tab.textContent = title;
      tab.addEventListener("click", () => this.select(page));
      tabs.append(tab);
    }
    this.select(this.page);
  }

  select(page) {
    this.page = page;
    for (const tab of this.tabsElement.children) tab.setAttribute("aria-selected", String(tab.dataset.page === page));
    // A narrow pane scrolls its tabs sideways: the one chosen shows.
    const strip = this.tabsElement.getBoundingClientRect();
    const chosen = this.tabsElement.querySelector(`[data-page="${page}"]`).getBoundingClientRect();
    if (chosen.right > strip.right) this.tabsElement.scrollLeft += chosen.right - strip.right;
    else if (chosen.left < strip.left) this.tabsElement.scrollLeft -= strip.left - chosen.left;
    this.list.hidden = page === "find";
    if (this.findPanel) this.findPanel.hidden = page !== "find";
    if (page !== "find") this.render();
  }

  // sources: [{ path, text }], the program's files first.
  update(sources) {
    this.sources = sources.map(({ path, text }) => ({ path, symbols: index(text) }));
    if (this.page !== "breakpoints" && this.page !== "find") this.render();
  }

  setBreakpoints(breakpoints) {
    this.breakpoints = breakpoints;
    if (this.page === "breakpoints") this.render();
  }

  render() {
    const list = this.list;
    const scroll = list.scrollTop;
    list.replaceChildren();
    const root = document.createElement("ul");
    root.className = "tree symbol-tree";
    const item = (text, path, line, depth, kind = "") => {
      const row = document.createElement("li");
      row.className = `symbol ${kind}`.trim();
      row.style.paddingLeft = `${4 + depth * 14}px`;
      const name = document.createElement("span");
      name.className = "name";
      name.textContent = text;
      row.append(name);
      row.title = `${path}, line ${line + 1}`;
      row.addEventListener("click", () => this.show(path, line));
      return row;
    };
    const groups = this.page === "breakpoints"
      ? this.breakpoints.map(({ path, rows }) => ({ path, rows: rows.map(({ row, text }) =>
        item(`${row}: ${text.trim() || "(empty line)"}`, path, row - 1, 1)) }))
      : this.sources.map(({ path, symbols }) => ({ path, rows: this.rows(symbols, path, item) }));
    for (const { path, rows } of groups) {
      if (!rows.length) continue;
      const key = `${this.page}|${path}`;
      const open = !this.closed.has(key);
      const header = document.createElement("li");
      header.className = "folder symbol-file";
      header.style.paddingLeft = "4px";
      header.textContent = `${open ? "▾" : "▸"} ${path}`;
      header.setAttribute("aria-expanded", String(open));
      header.addEventListener("click", () => {
        if (open) this.closed.add(key);
        else this.closed.delete(key);
        this.render();
      });
      root.append(header);
      if (open) root.append(...rows);
    }
    if (!root.children.length) {
      const empty = document.createElement("li");
      empty.className = "empty";
      empty.textContent = {
        functions: "No functions in the program's files.", types: "No types in the program's files.",
        labels: "No labels in the program's files.",
        breakpoints: "No breakpoints. Click the margin beside a line, or press F9, to set one.",
      }[this.page];
      root.append(empty);
    }
    list.append(root);
    list.scrollTop = scroll;
  }

  rows({ functions, types, labels }, path, item) {
    if (this.page === "functions") return functions.map((symbol) => item(symbol.name, path, symbol.line, 1));
    if (this.page === "labels") return labels.map((symbol) => item(`.${symbol.name}`, path, symbol.line, 1));
    const rows = [];
    for (const type of types) {
      rows.push(item(type.isEnum ? `${type.name} (Enum)` : type.name, path, type.line, 1, "type"));
      if (type.isEnum) continue;
      for (const field of type.fields) rows.push(item(field.name, path, field.line, 2, "field"));
      for (const method of type.methods) rows.push(item(`${method.name}()`, path, method.line, 2, "method"));
    }
    return rows;
  }
}
